/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { useNavigate } from 'react-router-dom'
import { collection, onSnapshot, query, orderBy, limit, where } from 'firebase/firestore'
import { db } from '../../firebase';
import { Box, Chip, Typography, useTheme, Grid, CircularProgress, Backdrop, useMediaQuery } from "@mui/material"
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { DataGrid, gridNumberComparator } from "@mui/x-data-grid"
import { tokens } from "../../theme"

// 日付範囲の初期値を計算する関数
const getInitialDateRange = () => {
  const today = dayjs();
  const currentYear = today.year();
  const currentMonth = today.month(); // 0-11

  let startDate, endDate;

  // 1月から3月の場合は前年度
  if (currentMonth < 3) { // 0, 1, 2 = 1月, 2月, 3月
    startDate = dayjs().year(currentYear - 1).month(3).date(1); // 前年の4月1日
    endDate = dayjs().year(currentYear).month(2).date(31); // 当年の3月31日
  } else {
    startDate = dayjs().year(currentYear).month(3).date(1); // 当年の4月1日
    endDate = dayjs().year(currentYear + 1).month(2).date(31); // 翌年の3月31日
  }

  return { startDate, endDate };
};

const ProductList = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const colors = tokens(theme.palette.mode)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [items, setItems] = useState<Array<object>>([])
  const [loading, setLoading] = useState<boolean>(true)
  const individualsRef = collection(db, 'individuals')

  // 日付範囲フィルターの初期値を設定
  const [dateRange, setDateRange] = useState(() => {
    const savedDateRange = sessionStorage.getItem('productListDateRange');
    if (savedDateRange) {
      const parsed = JSON.parse(savedDateRange);
      return {
        startDate: parsed.startDate ? dayjs(parsed.startDate) : null,
        endDate: parsed.endDate ? dayjs(parsed.endDate) : null
      };
    }
    return getInitialDateRange();
  });

  // 開始日の変更ハンドラー
  const handleStartDateChange = (newDate: dayjs.Dayjs | null) => {
    const newDateRange = { ...dateRange, startDate: newDate };
    setDateRange(newDateRange);
    // sessionStorageに保存
    sessionStorage.setItem('productListDateRange', JSON.stringify({
      startDate: newDate?.toISOString(),
      endDate: dateRange.endDate?.toISOString()
    }));
  };

  // 終了日の変更ハンドラー
  const handleEndDateChange = (newDate: dayjs.Dayjs | null) => {
    const newDateRange = { ...dateRange, endDate: newDate };
    setDateRange(newDateRange);
    // sessionStorageに保存
    sessionStorage.setItem('productListDateRange', JSON.stringify({
      startDate: dateRange.startDate?.toISOString(),
      endDate: newDate?.toISOString()
    }));
  };
  const columns = isMobile ? [
    {
      field: "No",
      headerName: "No",
      width: 50,
      sortComparator: gridNumberComparator,
    },
    {
      field: "individualNo",
      headerName: "個体番号",
      width: 120,
      cellClassName: "name-column--cell",
    },
    {
      field: "processingDate",
      headerName: "加工日",
      width: 100,
    },
    {
      field: "products",
      headerName: "製品",
      width: 70,
      renderCell: (params: any) => (
        <Chip label="製品" color="default" size="small" onClick={() => navigate(`/admin/products/${params.row.id}`)}></Chip>
      ),
    }
  ] : [
    {
      field: "No",
      headerName: "Mo",
      width: 10,
      sortComparator: gridNumberComparator,
    },
    {
      field: "individualNo",
      headerName: "個体識別番号",
      width: 200,
      cellClassName: "name-column--cell",
    },
    {
      field: "processingDate",
      headerName: "加工日",
      flex: 1,
    },
    {
      field: "sex",
      headerName: "性別",
      flex: 1,
    },
    {
      field: "age",
      headerName: "推定年齢",
      flex: 1,
    },
    {
      field: "weight",
      headerName: "体重",
      flex: 1,
      valueFormatter: (params: any) => {
        return params.value ? `${params.value} kg` : '';
      },
    },
    {
      field: "products",
      headerName: "製品情報",
      flex: 1,
      renderCell: (params: any) => (
        <Chip label="製品情報" color="default" size="small" onClick={() => navigate(`/admin/products/${params.row.id}`)}></Chip>
      ),
    },
    {
      field: "detail",
      headerName: "個体詳細",
      flex: 1,
      renderCell: (params: any) => (
        <Chip label="詳細" color="default" size="small" onClick={() => navigate(`/admin/individuals/${params.row.id}`)}></Chip>
      ),
    }
  ]

  useEffect(() => {
    if (!individualsRef) return

    let q;

    // 基本クエリ条件
    const baseQuery = query(
      individualsRef,
      where("status", "==", "processed")
    );

    // 日付範囲フィルター条件を追加
    if (dateRange.startDate && dateRange.endDate) {
      // 開始日と終了日の両方が設定されている場合
      const startDateStr = dateRange.startDate.format('YYYY-MM-DD');
      // 終了日は次の日の0時0分0秒の直前までを含める
      const nextDayStr = dateRange.endDate.add(1, 'day').format('YYYY-MM-DD');

      q = query(
        baseQuery,
        where("processingDate", ">=", startDateStr),
        where("processingDate", "<", nextDayStr),
        orderBy("processingDate", "desc"),
        limit(50)
      );
    } else if (dateRange.startDate) {
      // 開始日のみ設定されている場合
      const startDateStr = dateRange.startDate.format('YYYY-MM-DD');
      q = query(
        baseQuery,
        where("processingDate", ">=", startDateStr),
        orderBy("processingDate", "desc"),
        limit(50)
      );
    } else if (dateRange.endDate) {
      // 終了日のみ設定されている場合
      // 終了日は次の日の0時0分0秒の直前までを含める
      const nextDayStr = dateRange.endDate.add(1, 'day').format('YYYY-MM-DD');

      q = query(
        baseQuery,
        where("processingDate", "<", nextDayStr),
        orderBy("processingDate", "desc"),
        limit(50)
      );
    } else {
      // 日付範囲が設定されていない場合
      q = query(
        baseQuery,
        orderBy("processingDate", "desc"),
        limit(50)
      );
    }

    setLoading(true); // クエリ実行時にローディング表示

    // 10秒後に強制的にローディング表示を無効化（タイムアウト処理）
    const timeoutId = setTimeout(() => {
      setLoading(false);
    }, 10000);

    const unsubscribe = onSnapshot(
      q,
      ({ docs }) => {
        setItems(docs.map((_, i) => {
          return ({ No: i + 1, ..._.data(), id: _.id })
        }))
        setLoading(false); // データ取得完了時にローディング非表示
      },
      (error) => {
        console.error("Error fetching data:", error);
        setLoading(false); // エラー発生時もローディング非表示
      }
    )

    return () => {
      unsubscribe();
      clearTimeout(timeoutId); // タイムアウトをクリア
      setLoading(false); // クリーンアップ時にもローディング非表示
    }
  }, [dateRange]) // dateRangeが変更されたときにも再実行
  return (
    <Box m={isMobile ? "10px" : "20px"}>
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant={isMobile ? "h4" : "h3"}>製品一覧</Typography>

        <Grid container spacing={2} alignItems="center">
          {/* 日付範囲フィルター */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>期間</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
              <Box display="flex" alignItems="center" gap={1}>
                <DatePicker
                  label="開始日"
                  value={dateRange.startDate}
                  onChange={handleStartDateChange}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
                <Typography>～</Typography>
                <DatePicker
                  label="終了日"
                  value={dateRange.endDate}
                  onChange={handleEndDateChange}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Box>
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Box>

      {/* ローディングオーバーレイ */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box
        m={isMobile ? 1 : 2}
        height={isMobile ? "70vh" : "80vh"}
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid
          rows={items}
          columns={columns}
          loading={loading}
        />
      </Box>
    </Box>
  )
}
export default ProductList
